<script setup>
import Dialog from 'primevue/dialog';
import { ref, watch } from 'vue';
import { useUserStore } from '@/store/user-store';
import AnButton from '../common/AnButton.vue';

const props = defineProps({
  modelValue: Boolean,
  currentMonthlySIP: {
    type: Number,
    default: 0
  },
  currentWeightedAvgReturn: {
    type: Number,
    default: 0
  }
});

const emit = defineEmits(['update:modelValue']);
const store = useUserStore();

const isSIPUpdating = ref(false);
const sipError = ref('');
const sipFormData = ref({
  monthlySIP: 0,
  weightedAvgReturn: 0
});

// Watch for prop changes to pre-fill form
watch(() => props.modelValue, (isVisible) => {
  if (isVisible) {
    sipFormData.value = {
      monthlySIP: props.currentMonthlySIP,
      weightedAvgReturn: props.currentWeightedAvgReturn
    };
    sipError.value = '';
  }
});

const closeDialog = () => {
  emit('update:modelValue', false);
  sipError.value = '';
};

const handleSIPUpdate = async () => {
  sipError.value = '';

  // Validation
  if (sipFormData.value.monthlySIP < 0) {
    sipError.value = 'Monthly SIP amount cannot be negative';
    return;
  }

  if (sipFormData.value.weightedAvgReturn < 0 || sipFormData.value.weightedAvgReturn > 100) {
    sipError.value = 'Weighted Avg Return must be between 0 and 100';
    return;
  }

  isSIPUpdating.value = true;

  try {
    const payload = {
      monthlySIP: parseFloat(sipFormData.value.monthlySIP) || 0,
      weightedAvgSIPReturn: parseFloat(sipFormData.value.weightedAvgReturn) || 0
    };

    console.log('Updating SIP information:', payload);
    await store.updateFinancialStatement(payload);

    // Reload financial statement to get updated data
    await store.loadFinancialStatement(store.dashboardData.customerId);
    console.log('Financial statement reloaded after SIP update');

    store.fireAlert('Success', 'SIP information updated successfully!');

    // Small delay to ensure reactive updates propagate before closing
    await new Promise(resolve => setTimeout(resolve, 100));
    closeDialog();

  } catch (err) {
    sipError.value = 'Failed to update SIP information. Please try again.';
    console.error('SIP update error:', err);
  } finally {
    isSIPUpdating.value = false;
  }
};
</script>

<template>
  <Dialog
    modal
    :visible="modelValue"
    :closable="false"
    :dismissableMask="false"
    @update:visible="closeDialog"
    class="sip-dialog"
  >
    <i @click="closeDialog" class="pi pi-times close-btn" />
    <template #header>
      <div class="dialog-header">
        <span class="dialog-title">Manage SIP Information</span>
      </div>
    </template>
    <div class="dialog-content">
      <div class="dialog-section">
        <div class="dialog-row-flex">
          <div class="dialog-col">
            <label class="dialog-label">
              Monthly SIP Amount
              <i
                class="pi pi-info-circle info-icon"
                v-tooltip.top="'Total of all SIPs including MFs, LIC, PF, etc.'"
              ></i>
            </label>
            <input
              v-model="sipFormData.monthlySIP"
              class="dialog-input"
              type="number"
              placeholder="₹ Amount"
            />
          </div>
          <div class="dialog-col">
            <label class="dialog-label">
              Weighted Avg Return (%)
              <i
                class="pi pi-info-circle info-icon"
                v-tooltip.top="'Expected annual return percentage (0-100)'"
              ></i>
            </label>
            <input
              v-model="sipFormData.weightedAvgReturn"
              class="dialog-input"
              type="number"
              step="0.1"
              min="0"
              max="100"
              placeholder="% Return"
            />
          </div>
        </div>
        <div class="dialog-footer">
          <AnButton label="Cancel" variant="tab" @click="closeDialog" />
          <AnButton label="Update" variant="primary" @click="handleSIPUpdate" :loading="isSIPUpdating" />
        </div>
      </div>
      <div v-if="sipError" class="error-msg">{{ sipError }}</div>
    </div>
  </Dialog>
</template>

<style scoped>
.sip-dialog :deep(.p-dialog) {
  width: 500px !important;
  max-width: 500px !important;
  min-width: 320px !important;
}

.sip-dialog :deep(.p-dialog-content) {
  padding: 18px !important;
}

.dialog-header {
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  padding: 16px 0 0 0;
  min-height: 40px;
  border-bottom: 1px solid #e2e8f0;
}

.dialog-title {
  color: var(--an-primary);
  font-size: 1.2rem;
  font-weight: 700;
  flex: 1;
  text-align: center;
  margin: 0 auto;
  padding-left: 12px;
}

.close-btn {
  position: absolute;
  right: 10px;
  top: 10px;
  background: var(--an-error);
  color: #fff;
  padding: 6px 8px;
  border-radius: 5px;
  cursor: pointer;
  font-size: 1.2rem;
  z-index: 10;
}

.dialog-content {
  padding: 18px 18px 0 18px;
  min-height: 180px;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  width: 100%;
  box-sizing: border-box;
}

.dialog-section {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 16px 12px 24px 12px;
  margin-bottom: 10px;
  width: 100%;
  box-sizing: border-box;
}

.dialog-row-flex {
  display: flex;
  flex-direction: row;
  gap: 16px;
  margin-bottom: 24px;
}

.dialog-col {
  flex: 1 1 0;
  display: flex;
  flex-direction: column;
  min-width: 0;
  width: 100%;
}

.dialog-label {
  display: flex;
  align-items: center;
  gap: 4px;
  font-weight: 600;
  margin-bottom: 6px;
  font-size: 0.9rem;
  color: #334155;
}

.info-icon {
  font-size: 0.75rem;
  color: #94a3b8;
  cursor: help;
}

.dialog-input {
  padding: 10px 12px;
  border: 1.5px solid #cbd5e1;
  border-radius: 6px;
  font-size: 0.95rem;
  width: 100%;
  box-sizing: border-box;
  transition: border-color 0.2s ease;
}

.dialog-input:focus {
  outline: none;
  border-color: var(--an-primary);
  box-shadow: 0 0 0 3px rgba(13, 124, 104, 0.1);
}

.dialog-footer {
  display: flex;
  justify-content: center;
  gap: 12px;
  width: 100%;
  padding: 10px 0 0 0;
}

.error-msg {
  color: #dc2626;
  font-size: 0.85rem;
  margin: 0 12px 8px 12px;
  padding: 8px 12px;
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 4px;
}

/* Responsive for SIP Dialog */
@media (max-width: 768px) {
  .sip-dialog :deep(.p-dialog) {
    width: 95vw !important;
  }

  .dialog-row-flex {
    flex-direction: column;
    gap: 12px;
  }
}
</style>
